import { type GraphQLResult } from './apiClient';
import { authService } from './authService';
import { getApiClient } from './apiClient';

// Types
export interface UserTypeRole {
  user_type: string;
  role: string;
  role_description: string;
  is_enabled: boolean;
}

export interface UserTypeRolesResponse {
  roles: UserTypeRole[];
  availableRoles: string[];
  userTypes: string[];
}

// GraphQL Queries
const getUserTypeRolesQuery = /* GraphQL */ `
  query GetUserTypeRoles {
    getUserTypeRoles {
      roles {
        user_type
        role
        role_description
        is_enabled
      }
      availableRoles
      userTypes
    }
  }
`;

const assignUserTypeRoleMutation = /* GraphQL */ `
  mutation AssignUserTypeRole($userType: String!, $role: String!, $isEnabled: Boolean!) {
    assignUserTypeRole(userType: $userType, role: $role, isEnabled: $isEnabled) {
      result
      message
    }
  }
`;

const removeUserTypeRoleMutation = /* GraphQL */ `
  mutation RemoveUserTypeRole($userType: String!, $role: String!) {
    removeUserTypeRole(userType: $userType, role: $role) {
      result
      message
    }
  }
`;

// Response types
interface GetUserTypeRolesGraphQLResponse {
  getUserTypeRoles: UserTypeRolesResponse;
}

interface MutationResponse {
  result: boolean;
  message: string;
}

interface AssignRoleResponse {
  assignUserTypeRole: MutationResponse;
}

interface RemoveRoleResponse {
  removeUserTypeRole: MutationResponse;
}

export const roleService = {
  async checkAuth() {
    if (!authService.isAuthenticated()) {
      throw new Error('No authenticated user');
    }
    const groups = authService.getGroups();
    if (!groups.includes('Admin')) {
      throw new Error('User does not have Admin privileges');
    }
  },

  async getUserTypeRoles(): Promise<UserTypeRolesResponse> {
    try {
      await this.checkAuth();

      const response = await getApiClient().graphql({
        query: getUserTypeRolesQuery,
        authMode: 'userPool'
      }) as GraphQLResult<GetUserTypeRolesGraphQLResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.getUserTypeRoles;
    } catch (error) {
      console.error('Error fetching user type roles:', error);
      throw error;
    }
  },

  async assignRole(userType: string, role: string, isEnabled: boolean): Promise<MutationResponse> {
    try {
      await this.checkAuth();

      const response = await getApiClient().graphql({
        query: assignUserTypeRoleMutation,
        variables: { userType, role, isEnabled },
        authMode: 'userPool'
      }) as GraphQLResult<AssignRoleResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.assignUserTypeRole;
    } catch (error) {
      console.error('Error assigning role:', error);
      throw error;
    }
  },

  async removeRole(userType: string, role: string): Promise<MutationResponse> {
    try {
      await this.checkAuth();

      const response = await getApiClient().graphql({
        query: removeUserTypeRoleMutation,
        variables: { userType, role },
        authMode: 'userPool'
      }) as GraphQLResult<RemoveRoleResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.removeUserTypeRole;
    } catch (error) {
      console.error('Error removing role:', error);
      throw error;
    }
  }
};
